import { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { ArrowUpDown, Download, RotateCcw, Search, Columns3, ChevronLeft, ChevronRight, CalendarIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { format, subDays } from "date-fns";
import { toast } from "sonner";
import {
  FunnelBarChart, SourceDonutChart, VelocityAreaChart, StateBarChart,
  MilestoneFunnelChart, SourceMatrixChart, CounselorSlaChart, slaTone,
} from "../components/analytics/AnalyticsCharts";

const iso = (d) => format(d, "yyyy-MM-dd");
const OVERVIEW_DATE_PRESETS = [
  { v: "today", label: "Today" },
  { v: "7d", label: "Last 7 Days" },
  { v: "30d", label: "Last 30 Days" },
  { v: "custom", label: "Custom Range" },
];
function overviewPresetRange(v) {
  const now = new Date();
  if (v === "today") return { from: iso(now), to: iso(now) };
  if (v === "7d") return { from: iso(subDays(now, 6)), to: iso(now) };
  if (v === "30d") return { from: iso(subDays(now, 29)), to: iso(now) };
  return null; // custom — leave whatever the user already picked
}

function DateField({ value, onChange, testid, placeholder }) {
  const date = value ? new Date(value + "T00:00:00") : undefined;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" data-testid={testid} className="w-[150px] h-9 text-xs justify-start font-normal bg-white">
          <CalendarIcon className="w-3.5 h-3.5 mr-1.5 text-slate-400" />
          {date ? format(date, "dd MMM yyyy") : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={date} onSelect={(d) => onChange(d ? format(d, "yyyy-MM-dd") : "")} initialFocus />
      </PopoverContent>
    </Popover>
  );
}

const CARDS = [
  { key: "primary_leads", title: "Primary Leads", badge: "bg-blue-100 text-blue-800" },
  { key: "secondary_leads", title: "Secondary Leads", badge: "bg-indigo-100 text-indigo-800" },
  { key: "tertiary_leads", title: "Tertiary Leads", badge: "bg-purple-100 text-purple-800" },
  { key: "total_instances", title: "Total Instances", badge: "bg-slate-100 text-slate-800" },
  { key: "verified_leads", title: "Verified Leads", badge: "bg-emerald-100 text-emerald-800" },
  { key: "unverified_leads", title: "Unverified Leads", badge: "bg-amber-100 text-amber-800" },
  { key: "paid_applications", title: "Paid Applications", badge: "bg-teal-100 text-teal-800" },
  { key: "submitted_applications", title: "Submitted Applications", badge: "bg-blue-100 text-blue-800" },
];

const COLUMNS = [
  { key: "sno", label: "S.No", sortable: false },
  { key: "state", label: "State", sortable: true },
  { key: "primary_leads", label: "Primary Leads", sortable: true },
  { key: "verified_leads", label: "Verified Leads", sortable: true },
  { key: "unverified_leads", label: "Unverified Leads", sortable: true },
  { key: "form_initiated", label: "Form Initiated", sortable: true },
  { key: "payment_approved", label: "Payment Approved", sortable: true },
  { key: "conversion_pct", label: "Conversion %", sortable: true },
];

const EMPTY_FILTERS = { campus: "", source: "", instance: "", course: "", specialization: "", date_from: "", date_to: "" };

export default function Analytics() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [options, setOptions] = useState({ campuses: [], sources: [], instances: [], courses: [], specializations: [] });
  const [funnel, setFunnel] = useState({});
  const [dashboard, setDashboard] = useState({ funnel_stages: [], source_distribution: [], daily_trend: [], geographic_breakdown: [] });
  const [geo, setGeo] = useState({ rows: [], total: 0 });
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [datePreset, setDatePreset] = useState("custom");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ by: "primary_leads", dir: "desc" });
  const [page, setPage] = useState(1);
  const [visible, setVisible] = useState(() => Object.fromEntries(COLUMNS.map((c) => [c.key, true])));
  const [exporting, setExporting] = useState(false);
  const pageSize = 8;

  const params = useMemo(() => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => v && (p[k] = v));
    return p;
  }, [filters]);

  const fetchFunnel = useCallback(() => {
    api.get("/analytics/funnel", { params }).then((r) => setFunnel(r.data)).catch(() => {});
  }, [params]);

  const fetchDashboard = useCallback(() => {
    api.get("/analytics/dashboard", { params }).then((r) => setDashboard(r.data)).catch(() => {});
  }, [params]);

  const fetchGeo = useCallback(() => {
    api.get("/analytics/geo", {
      params: { ...params, search, sort_by: sort.by, sort_dir: sort.dir, page, page_size: pageSize },
    }).then((r) => setGeo(r.data)).catch(() => {});
  }, [params, search, sort, page]);

  const fetchOverview = useCallback(() => {
    setOverviewLoading(true);
    api.get("/analytics/overview", { params: { date_from: filters.date_from, date_to: filters.date_to, campus: filters.campus, course: filters.course } })
      // Counselor-only pages 403 here (leadership/performance data) — that's
      // expected for a counselor viewing /analytics, not an error to surface;
      // overview just stays null and its sections don't render.
      .then((r) => setOverview(r.data)).catch(() => setOverview(null)).finally(() => setOverviewLoading(false));
  }, [filters.date_from, filters.date_to, filters.campus, filters.course]);

  useEffect(() => { api.get("/analytics/filter-options").then((r) => setOptions(r.data)).catch(() => {}); }, []);
  useEffect(() => { fetchFunnel(); }, [fetchFunnel]);
  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);
  useEffect(() => { fetchGeo(); }, [fetchGeo]);
  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  const activeCount = Object.values(filters).filter(Boolean).length;

  const setF = (k, v) => {
    setPage(1);
    if (k === "date_from" || k === "date_to") setDatePreset("custom");
    setFilters((f) => ({ ...f, [k]: v === "__all__" ? "" : v }));
  };

  const applyDatePreset = (v) => {
    setDatePreset(v);
    setPage(1);
    const range = overviewPresetRange(v);
    if (range) setFilters((f) => ({ ...f, date_from: range.from, date_to: range.to }));
  };

  const toggleSort = (key) => {
    setPage(1);
    setSort((s) => (s.by === key ? { by: key, dir: s.dir === "asc" ? "desc" : "asc" } : { by: key, dir: "desc" }));
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const r = await api.get("/analytics/geo/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "geo_analytics.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV exported");
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(geo.total / pageSize));

  const filterSelect = (key, label, items, testid) => (
    <Select value={filters[key] || "__all__"} onValueChange={(v) => setF(key, v)}>
      <SelectTrigger data-testid={testid} className="w-[150px] h-9 text-xs bg-white">
        <SelectValue placeholder={label} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__all__">{label}: All</SelectItem>
        {(items || []).map((o) => (
          <SelectItem key={o} value={o}>{o}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  return (
    <div data-testid="analytics-page" className="space-y-6">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Publisher & Geographic Analytics</h1>
        <p className="text-sm text-slate-500 mt-1">Lead funnel attribution and state-wise conversion across all ingestion sources.</p>
      </div>

      <Card className="p-4 sticky top-[4.5rem] z-10 shadow-sm border-slate-200" data-testid="analytics-filter-bar">
        <div className="flex flex-wrap items-center gap-2">
          <Select value={datePreset} onValueChange={applyDatePreset}>
            <SelectTrigger data-testid="analytics-date-preset-select" className="w-[150px] h-9 text-xs bg-white">
              <SelectValue placeholder="Date range" />
            </SelectTrigger>
            <SelectContent>
              {OVERVIEW_DATE_PRESETS.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {filterSelect("campus", "Campus", options.campuses, "analytics-filter-campus-select")}
          {filterSelect("source", "Source", options.sources, "analytics-filter-source-select")}
          {filterSelect("instance", "Instance", options.instances, "analytics-filter-instance-select")}
          {filterSelect("course", "Course", options.courses, "analytics-filter-course-select")}
          {filterSelect("specialization", "Specialization", options.specializations, "analytics-filter-specialization-select")}
          <DateField testid="analytics-filter-date-from" value={filters.date_from} placeholder="From date"
            onChange={(v) => setF("date_from", v)} />
          <DateField testid="analytics-filter-date-to" value={filters.date_to} placeholder="To date"
            onChange={(v) => setF("date_to", v)} />
          {activeCount > 0 && (
            <Button variant="ghost" size="sm" data-testid="analytics-filter-reset"
              onClick={() => { setFilters(EMPTY_FILTERS); setPage(1); }} className="text-slate-500">
              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset ({activeCount})
            </Button>
          )}
        </div>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="funnel-metric-cards">
        {CARDS.map((c) => (
          <Card key={c.key} className="p-5 border-slate-200 hover:shadow-md transition-shadow" data-testid={`metric-card-${c.key}`}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{c.title}</span>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${c.badge}`}>LIVE</span>
            </div>
            <div className="font-mono text-3xl font-bold tracking-tight text-slate-900">
              {(funnel[c.key] ?? 0).toLocaleString("en-IN")}
            </div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="analytics-charts">
        <div className="lg:col-span-2"><FunnelBarChart data={dashboard.funnel_stages} /></div>
        <div className="lg:col-span-1"><SourceDonutChart data={dashboard.source_distribution} /></div>
        <div className="lg:col-span-2"><VelocityAreaChart data={dashboard.daily_trend} /></div>
        <div className="lg:col-span-1"><StateBarChart data={dashboard.geographic_breakdown} /></div>
      </div>

      {(overviewLoading && !overview) ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="overview-skeleton">
          {[0, 1, 2].map((i) => (
            <Card key={i} className="p-5 border-slate-200 space-y-3">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-48 w-full" />
            </Card>
          ))}
        </div>
      ) : overview ? (
        <div className="space-y-4" data-testid="overview-section">
          <h2 className="text-lg font-semibold text-slate-800">Live Funnel & Attribution — leadership view</h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <MilestoneFunnelChart data={overview.funnel} />
            <SourceMatrixChart data={overview.source_attribution} />
          </div>

          <Card className="border-slate-200" data-testid="source-attribution-table-card">
            <div className="p-4 border-b border-slate-200">
              <h3 className="text-sm font-semibold text-slate-800">Source Attribution — executive table</h3>
              <p className="text-xs text-slate-400 mt-0.5">
                CPL / CAC need marketing spend data, which isn't tracked anywhere in EduPulse yet — shown as "—" rather than guessed.
              </p>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  {["Source", "Gross", "Primary", "Secondary", "Tertiary", "Dedup Efficiency", "Enrolled", "Conversion", "CPL", "CAC"].map((h) => (
                    <TableHead key={h} className="text-xs font-semibold uppercase tracking-wider text-slate-500">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {overview.source_attribution.length === 0 && (
                  <TableRow><TableCell colSpan={10} className="text-center py-8 text-slate-400">No data for the current filters.</TableCell></TableRow>
                )}
                {overview.source_attribution.map((s) => (
                  <TableRow key={s.source} data-testid={`source-attr-row-${s.source}`} className="hover:bg-slate-50/60">
                    <TableCell className="font-medium text-slate-800">{s.source}</TableCell>
                    <TableCell className="font-mono">{s.gross_inquiries}</TableCell>
                    <TableCell className="font-mono">{s.primary_leads}</TableCell>
                    <TableCell className="font-mono text-slate-500">{s.secondary_leads}</TableCell>
                    <TableCell className="font-mono text-slate-500">{s.tertiary_leads}</TableCell>
                    <TableCell className="font-mono text-emerald-700">{s.dedup_efficiency_pct}%</TableCell>
                    <TableCell className="font-mono text-teal-700">{s.enrolled_count}</TableCell>
                    <TableCell className="font-mono">{s.conversion_pct}%</TableCell>
                    <TableCell className="text-slate-300">—</TableCell>
                    <TableCell className="text-slate-300">—</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <CounselorSlaChart data={overview.counselor_sla} />
            <Card className="border-slate-200" data-testid="counselor-sla-table-card">
              <div className="p-4 border-b border-slate-200">
                <h3 className="text-sm font-semibold text-slate-800">Counselor Leaderboard</h3>
                <p className="text-xs text-slate-400 mt-0.5">First-contact SLA compliance · avg hours to doc verification</p>
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    {["Counselor", "Assigned", "SLA Compliance", "Avg Doc-Verify (h)", "Enrolled"].map((h) => (
                      <TableHead key={h} className="text-xs font-semibold uppercase tracking-wider text-slate-500">{h}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {overview.counselor_sla.length === 0 && (
                    <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-400">No counselors yet.</TableCell></TableRow>
                  )}
                  {overview.counselor_sla.map((c) => (
                    <TableRow key={c.counselor_id} data-testid={`counselor-sla-row-${c.counselor_id}`} className="hover:bg-slate-50/60">
                      <TableCell className="font-medium text-slate-800">{c.name}</TableCell>
                      <TableCell className="font-mono">{c.total_assigned_leads}</TableCell>
                      <TableCell>
                        <Badge className={slaTone(c.sla_compliance_pct).cls} data-testid={`sla-badge-${c.counselor_id}`}>
                          {c.sla_compliance_pct == null ? "No data" : `${c.sla_compliance_pct}%`}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono text-slate-500">
                        {c.avg_doc_verification_hours == null ? "—" : c.avg_doc_verification_hours}
                      </TableCell>
                      <TableCell className="font-mono text-teal-700">{c.enrolled_count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </div>
        </div>
      ) : null}

      <Card className="border-slate-200" data-testid="geo-table-card">
        <div className="p-4 flex flex-wrap items-center gap-3 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-800 w-full sm:w-auto sm:mr-auto">State-wise Breakdown</h2>
          <div className="relative flex-1 sm:flex-none min-w-[9rem]">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
            <Input data-testid="geo-search-input" placeholder="Search state…" value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="pl-8 h-9 w-full sm:w-48 text-sm" />
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="geo-column-toggle-button">
                <Columns3 className="w-4 h-4 mr-1.5" /> Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {COLUMNS.map((c) => (
                <DropdownMenuCheckboxItem key={c.key} checked={visible[c.key]}
                  onCheckedChange={(v) => setVisible((s) => ({ ...s, [c.key]: v }))}>
                  {c.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button size="sm" data-testid="geo-export-csv-button" onClick={exportCsv} disabled={exporting}
            className="bg-brand">
            <Download className="w-4 h-4 mr-1.5" /> {exporting ? "Exporting…" : "CSV Export"}
          </Button>
        </div>

        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              {COLUMNS.filter((c) => visible[c.key]).map((c) => (
                <TableHead key={c.key}>
                  {c.sortable ? (
                    <button data-testid={`geo-sort-${c.key}`} onClick={() => toggleSort(c.key)}
                      className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-900">
                      {c.label}
                      <ArrowUpDown className={`w-3 h-3 ${sort.by === c.key ? "text-blue-600" : "text-slate-300"}`} />
                    </button>
                  ) : (
                    <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{c.label}</span>
                  )}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {geo.rows.length === 0 && (
              <TableRow><TableCell colSpan={COLUMNS.length} className="text-center py-10 text-slate-400" data-testid="geo-empty-state">No data matches the current filters.</TableCell></TableRow>
            )}
            {geo.rows.map((r, i) => (
              <TableRow key={r.state} data-testid={`geo-row-${i}`} className="hover:bg-slate-50/60">
                {visible.sno && <TableCell className="text-slate-400 font-mono text-xs">{(page - 1) * pageSize + i + 1}</TableCell>}
                {visible.state && <TableCell className="font-medium text-slate-800">{r.state}</TableCell>}
                {visible.primary_leads && <TableCell className="font-mono">{r.primary_leads}</TableCell>}
                {visible.verified_leads && <TableCell className="font-mono text-emerald-700">{r.verified_leads}</TableCell>}
                {visible.unverified_leads && <TableCell className="font-mono text-amber-700">{r.unverified_leads}</TableCell>}
                {visible.form_initiated && <TableCell className="font-mono">{r.form_initiated}</TableCell>}
                {visible.payment_approved && <TableCell className="font-mono text-teal-700">{r.payment_approved}</TableCell>}
                {visible.conversion_pct && (
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-blue-600 rounded-full" style={{ width: `${Math.min(r.conversion_pct, 100)}%` }} />
                      </div>
                      <span className="font-mono text-xs">{r.conversion_pct}%</span>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <div className="p-4 flex items-center justify-between border-t border-slate-200">
          <span className="text-xs text-slate-500" data-testid="geo-pagination-info">
            Page {page} of {totalPages} · {geo.total} states
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" data-testid="geo-prev-page" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" data-testid="geo-next-page" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
